package com.milta.feature.route.data.remote.dto

import com.milta.feature.route.domain.entity.DeliveryOutcome
import com.milta.feature.route.domain.entity.DeliveryProofHistoryItem
import com.milta.feature.route.domain.entity.RouteProofsResult
import com.milta.feature.route.domain.entity.RouteProofsSummary
import kotlinx.serialization.Serializable

/**
 * Mirrors backend `DeliveryProofItemDto` — one card of `GET /api/v1/routes/{routeId}/proofs`
 * (MIL-52, Screen B). [status] decodes directly to [DeliveryOutcome] (its wire values "Completed"/
 * "Failed" already match the enum constant names, no `@SerialName` needed) rather than a raw
 * String + `when`, per the enum-not-magic-strings rule.
 */
@Serializable
data class DeliveryProofItemDto(
    val deliveryId: String,
    val status: DeliveryOutcome,
    val completedAt: String,
    val recipientName: String,
    val address: String,
    val proofPhotoUrl: String? = null,
    val comment: String? = null,
)

fun DeliveryProofItemDto.toDomain() = DeliveryProofHistoryItem(
    deliveryId = deliveryId,
    status = status,
    completedAt = completedAt,
    recipientName = recipientName,
    address = address,
    proofPhotoUrl = proofPhotoUrl,
    comment = comment,
)

/** Mirrors backend's `route` summary object embedded in `GET /routes/{routeId}/proofs` (MIL-52). */
@Serializable
data class RouteProofsSummaryDto(
    val date: String,
    val driverFirstName: String,
    val driverLastName: String,
    val totalDeliveries: Int,
    val deliveredCount: Int,
    val failedCount: Int,
)

fun RouteProofsSummaryDto.toDomain() = RouteProofsSummary(
    date = date,
    driverFirstName = driverFirstName,
    driverLastName = driverLastName,
    totalDeliveries = totalDeliveries,
    deliveredCount = deliveredCount,
    failedCount = failedCount,
)

/** Mirrors the full backend response envelope of `GET /api/v1/routes/{routeId}/proofs` (MIL-52). */
@Serializable
data class RouteProofsResponseDto(
    val route: RouteProofsSummaryDto,
    val proofs: PagedResultDto<DeliveryProofItemDto>,
)

fun RouteProofsResponseDto.toDomain() = RouteProofsResult(
    route = route.toDomain(),
    proofs = proofs.toDomain { it.toDomain() },
)
